import React from 'react';
import { calculateSunnyScore, getSunnyScoreCategory, getSunnyScoreEmoji } from './SunnyScore';

/**
 * Komponente zur Anzeige der Wettervorhersage für das Wochenende
 * @param {Object} props - Component props
 * @param {Array} props.weekendData - Daten für das Wochenendwetter
 * @returns {JSX.Element} WeekendForecast-Komponente
 */
const WeekendForecast = ({ weekendData }) => {
  if (!weekendData || weekendData.length === 0) {
    return (
      <div className="mb-8 text-center">
        <h2 className="text-xl font-bold mb-2">Wochenendvorhersage</h2>
        <p>Keine Daten für das kommende Wochenende verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center mb-4">
        <span className="text-xl mr-3" role="img" aria-label="weekend">📅</span>
        <h2 className="text-xl font-semibold text-gray-800">Wochenendvorhersage</h2>
      </div>
      
      {/* Flex-Container statt Grid für Desktop-Layout */}
      <div className="flex flex-col md:flex-row gap-4">
        {weekendData.map((day, index) => {
          // SunnyScore für jeden Tag berechnen
          const score = calculateSunnyScore(
            day.sonneneinstrahlung,
            day.niederschlag,
            day.luftfeuchtigkeit,
            day.windgeschwindigkeit,
            new Date(day.datum).getMonth() + 1
          );
          
          const category = getSunnyScoreCategory(score);
          const emoji = getSunnyScoreEmoji(score);
          
          // Korrigierte Farbenlogik basierend auf dem Score
          // "Sehr gut" (Score >= 70): Hellgrün
          // "Gut" (Score >= 55): Hellgelb
          // "Mäßig" (Score < 55): Hellrot
          let cardBackground;
          if (score >= 70) {
            cardBackground = 'bg-green-50';
          } else if (score >= 55) {
            cardBackground = 'bg-yellow-50';
          } else {
            cardBackground = 'bg-red-50';
          }
          
          return (
            <div 
              key={index} 
              className={`flex-1 rounded-xl p-2 sm:p-4 shadow-sm border border-gray-200 ${cardBackground}`}
            >
              <div className="text-center mb-3 sm:mb-4">
                <h3 className="font-bold text-sm sm:text-base">{day.wochentag}</h3>
                <p className="text-xs text-gray-600">{formatDate(day.datum)}</p>
              </div>
              
              {/* Score-Kreis mit Fortschrittsanzeige beginnend bei 12 Uhr */}
              <div className="flex justify-center items-center mb-3 sm:mb-4">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                  {/* Äußerer Kreis mit Fortschritt */}
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    {/* Grauer Hintergrundkreis */}
                    <circle 
                      cx="50" 
                      cy="50" 
                      r="45" 
                      fill="transparent" 
                      stroke="#e5e7eb" 
                      strokeWidth="10" 
                    />
                    
                    {/* Fortschrittskreis mit der korrekten Farbe basierend auf dem Score */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="transparent"
                      stroke={score >= 70 ? "#4CAF50" : (score >= 55 ? "#FFB400" : "#FF5A5F")}
                      strokeWidth="10"
                      strokeDasharray={`${score * 2.83} ${283 - score * 2.83}`}
                      strokeDashoffset="0"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  
                  {/* Weißer Innenkreis mit Score */}
                  <div 
                    className="absolute inset-0 flex flex-col items-center justify-center"
                    style={{
                      top: '15%', 
                      left: '15%', 
                      width: '70%', 
                      height: '70%', 
                      borderRadius: '50%', 
                      backgroundColor: 'white'
                    }}
                  >
                    <div className="text-xl sm:text-2xl font-bold">{score}</div>
                    <div className="text-base sm:text-lg">{emoji}</div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mb-3 sm:mb-4">
                <p className="font-semibold text-sm sm:text-base">{category}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-1 sm:gap-2 text-center">
                <div className="p-1 sm:p-2 bg-white rounded">
                  <p className="text-xxs sm:text-xs text-gray-600">Temp</p>
                  <p className="text-xs sm:font-semibold">{Math.round(day.temperatur)}°C</p>
                </div>
                <div className="p-1 sm:p-2 bg-white rounded">
                  <p className="text-xxs sm:text-xs text-gray-600">Sonne</p>
                  <p className="text-xs sm:font-semibold">{day.sonnenstunden}h</p>
                </div>
                <div className="p-1 sm:p-2 bg-white rounded">
                  <p className="text-xxs sm:text-xs text-gray-600">Regen</p>
                  <p className="text-xs sm:font-semibold">{Math.round(day.regenwahrscheinlichkeit)}%</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Hilfsfunktion zum Formatieren eines Datums
 * @param {string} dateStr - Datumstring im Format 'YYYY-MM-DD'
 * @returns {string} Formatiertes Datum (DD.MM.)
 */
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.`;
}

export default WeekendForecast;